<template>
  <div class="cms-container">
    <div class="cms-content">
      <div class="cms-sidebar">
        <div class="cms-nav">
          <button 
            v-for="item in cmsMenuItems" 
            :key="item.name"
            class="cms-nav-item"
            :class="{ active: activeTab === item.name }"
            @click="setActiveTab(item.name)"
          >
            <v-icon :icon="item.icon" size="sm" class="cms-nav-icon" />
            <span class="cms-nav-text">{{ $t(item.label) }}</span>
          </button>
        </div>
      </div>

      <div class="cms-main">
        <div class="cms-tab-content">
          <!-- Show router-view for all child routes meanwhile we keep the sidebar -->
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

export default defineComponent({
  name: 'TheCms',
  components: { VIcon },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const activeTab = ref('dashboard');

    const cmsMenuItems = [
      {
        name: 'dashboard',
        routeName: 'cms.dashboard',
        label: 'cms.menu.dashboard',
        icon: 'home'
      },
      {
        name: 'activities',
        routeName: 'cms.activities',
        label: 'cms.menu.activities',
        icon: 'map'
      },
      {
        name: 'stories',
        routeName: 'cms.stories',
        label: 'cms.menu.stories',
        icon: 'quote'
      },
      {
        name: 'guiders',
        routeName: 'cms.guiders',
        label: 'cms.menu.guides',
        icon: 'compass'
      },
      {
        name: 'team-members',
        routeName: 'cms.team-members',
        label: 'cms.menu.team',
        icon: 'user'
      },
      {
        name: 'reviews',
        routeName: 'cms.reviews',
        label: 'cms.menu.reviews',
        icon: 'happy'
      },
      {
        name: 'contact-messages',
        routeName: 'cms.contact-messages',
        label: 'cms.menu.contactMessages',
        icon: 'mail'
      },
      {
        name: 'help-center',
        routeName: 'cms.help-center',
        label: 'cms.menu.faq',
        icon: 'question'
      },
      {
        name: 'pages',
        routeName: 'cms.pages',
        label: 'cms.menu.pages',
        icon: 'description'
      },
      {
        name: 'menus',
        routeName: 'cms.menus',
        label: 'cms.menu.menus',
        icon: 'hamburger'
      },
      {
        name: 'media',
        routeName: 'cms.media',
        label: 'cms.menu.media',
        icon: 'picture'
      },
      {
        name: 'settings',
        routeName: 'cms.settings',
        label: 'cms.menu.settings',
        icon: 'settings'
      }
    ];

    // Watch route changes to update active tab
    watch(() => route.name, (newRouteName) => {
      if (newRouteName && typeof newRouteName === 'string') {
        // Extract the section from route name (e.g., 'cms.activities' -> 'activities')
        const section = newRouteName.split('.')[1];
        if (section) {
          activeTab.value = section;
        }
      }
    }, { immediate: true });

    const setActiveTab = (tabName: string) => {
      const menuItem = cmsMenuItems.find(item => item.name === tabName);
      if (menuItem) {
        router.push({ name: menuItem.routeName });
      }
    };

    return {
      activeTab,
      cmsMenuItems,
      setActiveTab
    };
  }
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/tokens.scss';

.cms-container {
  min-height: 100vh;
  color: $cms-text-primary;
  // padding-right: var(--uq-gutter-x, 1rem);
  // padding-left: var(--uq-gutter-x, 1rem);
  padding-top: 0.5rem;
  margin-right:auto;
  margin-left:auto;
  max-width: 1600px;
}

.cms-header {
  background: $cms-background-overlay;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid $cms-border-color-white;
  padding: 2rem 0;
}

.cms-header-content {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
}

.cms-title {
  display: flex;
  align-items: center;
  // gap: 1rem;
  font-size: $cms-font-size-3xl;
  font-weight: 700;
  margin: 0;
}

.cms-icon {
  color: $cms-primary-color;
}

.cms-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0.5rem 0 0 0;
}

.cms-content {
  display: flex;
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
  gap: 1rem;
  min-height: calc(100vh - 200px);
}

.cms-sidebar {
  width: fit-content;
  min-width:160px;
  flex-shrink: 0;
  // background: $cms-background-gray-light;  
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
  // border-radius: 16px;
  // border: 1px solid rgba(0, 0, 0, 0.15);
}

.cms-nav {
  border-radius: 10px;
  padding: 0;
  border: 1px solid $cms-border-color-light;   
   background: $cms-background-gray-light;

}

.cms-nav-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  color: $cms-text-primary;
  transition: all 0.3s ease;
  font-weight: 500;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: $cms-background-overlay-hover;
    color: #FD0C6F;
  }

  &.active {
    background: rgba(253, 12, 111, 0.12);
    color: #222422;
    &:hover {
      background: rgba(253, 12, 111, 0.16);
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.cms-nav-icon {
  color: #F09D57;
}

.cms-nav-item.active .cms-nav-icon {
  color: #FD0C6F;
}

.cms-nav-text {
  font-size: 0.9rem;
  line-height: 1.3;
}

.cms-main {
  flex: 1;
  border-radius: 10px;
  padding: 2rem;
  padding-top:0rem;
  border: 1px solid $cms-border-color-light;
  background-color: $cms-background-gray;
  // overflow: hidden;
}

.cms-tab-content {
  height: 100%;
}

.cms-tab-panel {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .cms-content {
    flex-direction: column;
    padding: 1rem;
  }

  .cms-sidebar {
    width: 100%;
  }

  .cms-nav {
    display: flex;
    overflow-x: auto;
    padding: 1rem;
  }

  .cms-nav-item {
    flex-shrink: 0;
    margin-bottom: 0;
    margin-right: 0.5rem;
    min-width: 120px;
  }

  .cms-title {
    font-size: $cms-font-size-2xl;
  }

  .cms-main {
    padding: 1rem;
  }
}
</style> 